import { AlertCircle } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useUploadFile } from "../api/upload-file";

type UploadInputProps = {
  onChange: (fileName: string) => void;
  value?: string;
  allowedFileTypes?: string[];
  "aria-disabled"?: boolean;
  name: string;
  maxSizeInMB?: number;
};

const UploadInput = (props: UploadInputProps) => {
  const {
    onChange,
    "aria-disabled": isDisabled,
    value,
    allowedFileTypes,
    name,
    maxSizeInMB = 1,
  } = props;

  const {
    mutateAsync: uploadFile,
    data: uploadedFile,
    isSuccess,
    isPending: isUploading,
    reset,
    error,
    isError,
  } = useUploadFile();
  const form = useFormContext();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    form.clearErrors(name);

    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];

      // File size validation
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > maxSizeInMB) {
        form.setError(name ?? "root", {
          message: `File size must be less than ${maxSizeInMB}MB`,
        });
        return;
      }

      if (allowedFileTypes && !allowedFileTypes.includes(file.type)) {
        form.setError(name ?? "root", {
          message: "File type not allowed",
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await uploadFile({
        fileName: file.name,
        file: file,
      });

      if (response) {
        onChange(response.reference);
      }
    }
  };

  if (isSuccess) {
    return (
      <div ref={form.register(name).ref} className="flex items-start gap-4">
        <img
          src={uploadedFile.reference}
          alt={uploadedFile.filename}
          className="h-24 rounded-xl"
        />

        <Button
          variant="destructive"
          type="button"
          onClick={() => {
            reset();
            onChange("");
          }}
          disabled={isDisabled}
        >
          Remove
        </Button>
      </div>
    );
  }

  if (value && typeof value === "string") {
    return (
      <div ref={form.register(name).ref} className="flex items-start gap-4">
        <img src={value} alt={value} className="h-24 rounded-xl" />

        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            type="button"
            onClick={() => {
              reset();
              onChange("");
            }}
            disabled={isDisabled}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* <Button
        variant="outline"
        type="button"
        disabled={isDisabled || isUploading}
        isLoading={isUploading}
        onClick={() => {
          inputRef.current?.click();
        }}
        isAnimated
      >
        <Upload className="mr-2 size-4" /> Upload file
      </Button> */}
      <Input
        className="h-8 w-96 px-2 py-1"
        disabled={isDisabled || isUploading}
        type="file"
        onChange={handleFileChange}
        ref={form.register(name).ref}
      />

      {isError && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Upload failed</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

UploadInput.displayName = "UploadInput";

export default UploadInput;
